import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SPEECH_ERROR_MESSAGES, VOICE_UNSUPPORTED } from "../services/speech/speech.copy";
import type { SpeechErrorCode, SpeechInputProvider, SpeechInputSession } from "../services/speech/speech.types";
import { getSpeechProvider } from "../services/speech/speechProviders";

export interface UseSpeechInputOptions {
  /** Defaults to the app's provider; a test or a host can pass its own. */
  provider?: SpeechInputProvider;
  /** BCP 47 tag; defaults to the browser's language. */
  lang?: string;
}

export interface SpeechInputState {
  isSupported: boolean;
  /** Why voice input can't be used, or null. */
  unsupportedReason: string | null;
  isListening: boolean;
  /** Everything heard this session: finished phrases plus the one in progress. */
  transcript: string;
  /** Replaces the transcript (the user edited it while listening). */
  setTranscript: (text: string) => void;
  /** The last failure, until the next start. `aborted` (our own cancel) is not one. */
  error: SpeechErrorCode | null;
  errorMessage: string | null;
  start: () => void;
  /** Ends listening; the transcript stays. */
  stop: () => void;
  /** Ends listening and drops the transcript. */
  abort: () => void;
}

const join = (finals: string, partial: string): string => [finals, partial].filter(Boolean).join(" ");

/** Drives a `SpeechInputProvider` from a component. It only fills text: sending is never its job. */
export const useSpeechInput = ({ provider, lang }: UseSpeechInputOptions = {}): SpeechInputState => {
  const active = useMemo(() => provider ?? getSpeechProvider(), [provider]);
  const isSupported = active.isSupported();
  const [isListening, setIsListening] = useState(false);
  const [finals, setFinals] = useState("");
  const [partial, setPartial] = useState("");
  const [error, setError] = useState<SpeechErrorCode | null>(null);
  const sessionRef = useRef<SpeechInputSession | null>(null);
  // Events of a session that was replaced or aborted are dropped.
  const tokenRef = useRef(0);

  const end = useCallback(() => {
    tokenRef.current += 1;
    sessionRef.current = null;
    setIsListening(false);
    setPartial("");
  }, []);

  const start = useCallback(() => {
    if (sessionRef.current || !active.isSupported()) return;
    tokenRef.current += 1;
    const token = tokenRef.current;
    const live = () => tokenRef.current === token;
    setFinals("");
    setPartial("");
    setError(null);
    setIsListening(true);
    const session = active.start(
      { lang: lang ?? ((typeof navigator !== "undefined" ? navigator.language : "") || "en-US") },
      {
        onPartial: (text) => live() && setPartial(text),
        onFinal: (text) => {
          if (!live() || !text) return;
          setFinals((prev) => join(prev, text));
          setPartial("");
        },
        onError: (code) => live() && code !== "aborted" && setError(code),
        onEnd: () => {
          if (!live()) return;
          tokenRef.current += 1;
          sessionRef.current = null;
          setIsListening(false);
          setPartial("");
        },
      },
    );
    // A start that failed at once has already ended (and cleared the ref): don't keep its stub.
    if (live()) sessionRef.current = session;
  }, [active, lang]);

  const stop = useCallback(() => sessionRef.current?.stop(), []);

  const abort = useCallback(() => {
    const session = sessionRef.current;
    end();
    setFinals("");
    session?.abort();
  }, [end]);

  useEffect(() => () => {
    tokenRef.current += 1;
    sessionRef.current?.abort();
    sessionRef.current = null;
  }, []);

  const setTranscript = useCallback((text: string) => {
    setFinals(text);
    setPartial("");
  }, []);

  return {
    isSupported,
    unsupportedReason: isSupported ? null : VOICE_UNSUPPORTED,
    isListening,
    transcript: join(finals, partial),
    setTranscript,
    error,
    errorMessage: error && error !== "aborted" ? SPEECH_ERROR_MESSAGES[error] : null,
    start,
    stop,
    abort,
  };
};
